import React, { useEffect, useState } from 'react';
import { Card, Upload, Button, Select, List, Tag, Popconfirm, App, Spin, Input, Modal, Row, Col, Empty } from 'antd';
import { UploadOutlined, FilePdfOutlined, DeleteOutlined, ThunderboltOutlined, EditOutlined, CheckOutlined, CloseOutlined, EyeOutlined, SearchOutlined, FileTextOutlined, FilePptOutlined, FileWordOutlined } from '@ant-design/icons';
import AppLayout from '../components/AppLayout';
import { useUser } from '../context/UserContext';
import api from '../api/client';
import dayjs from 'dayjs';
import AIGeneratorPanel from '../components/AIGeneratorPanel';

// Single source of truth for material type options — used by the filter, upload modal, and AI generator.
const TYPE_OPTIONS = [
  { value: 'questions',   label: 'Questions' },
  { value: 'solutions',   label: 'Solutions' },
  { value: 'notes',       label: 'Notes' },
  { value: 'exam_paper',  label: 'Exam paper' },
  { value: 'mark_scheme', label: 'Mark scheme' },
];

export default function Material() {
  const { user }    = useUser();
  const { message } = App.useApp();
  const [materials, setMaterials]   = useState([]);
  const [subjects, setSubjects]     = useState([]);
  const [tutors, setTutors]         = useState([]);
  const [selSubject, setSelSubject] = useState(null);
  const [selTutor, setSelTutor]     = useState(null);
  const [selAiType, setSelAiType]   = useState(null);
  const [search, setSearch]         = useState('');
  const [uploading, setUploading]   = useState(false);
  const [loading, setLoading]       = useState(true);
  const [aiOpen, setAiOpen]           = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadName, setUploadName]   = useState('');
  const [editingId, setEditingId]     = useState(null);
  const [editingName, setEditingName] = useState('');
  const [selUploadType, setSelUploadType] = useState(null);

  const loadAll = async () => {
    try {
      if (user.role === 'student') {
        const mats = await api.get('/api/materials');
        setMaterials(mats.data);
        // Derive unique tutors from the materials
        const seenTutors = new Set();
        const derivedTutors = [];
        for (const m of mats.data) {
          if (m.tutor_name && !seenTutors.has(m.tutor_name)) {
            seenTutors.add(m.tutor_name);
            derivedTutors.push({ tutor_name: m.tutor_name });
          }
        }
        setTutors(derivedTutors);
        setSubjects([]);
      } else {
        const [mats, subs] = await Promise.all([
          api.get('/api/materials', { params: { user_id: user.userId } }),
          api.get(`/api/users/${user.userId}/subjects`),
        ]);
        setMaterials(mats.data);
        setSubjects(subs.data);
      }
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [user]);

  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const ALLOWED_EXT   = /\.(pdf|jpg|jpeg|png|doc|docx)$/i;

  const handleUpload = (file) => {
    if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXT.test(file.name)) {
      message.error('Only PDF, Word, and image files (JPG, PNG) are allowed.');
      return false;
    }
    setPendingFile(file);
    setUploadName(file.name);
    setSelSubject(null);
    setSelUploadType(null);
    setUploadModal(true);
    return false;
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const urlRes = await api.get('/api/materials/upload-url', {
        params: { fileName: `${Date.now()}_${pendingFile.name}`, fileType: pendingFile.type },
      });
      await fetch(urlRes.data.uploadURL, { method: 'PUT', body: pendingFile, headers: { 'Content-Type': pendingFile.type } });
      const s3Key = urlRes.data.uploadURL.split('?')[0];
      await api.post('/api/materials', {
        user_id: user.userId, subject_id: selSubject,
        material_link: s3Key, file_name: uploadName.trim() || pendingFile.name,
        ai_type: selUploadType || null,
      });
      message.success('Uploaded successfully');
      setUploadModal(false);
      setPendingFile(null);
      setUploadName('');
      setSelSubject(null);
      setSelUploadType(null);
      loadAll();
    } catch { message.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleRename = async (id) => {
    if (!editingName.trim()) { setEditingId(null); return; }
    try {
      await api.patch(`/api/materials/${id}`, { file_name: editingName.trim() });
      setEditingId(null);
      loadAll();
    } catch { message.error('Rename failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/api/materials/${id}`); message.success('Deleted'); loadAll(); }
    catch { message.error('Delete failed'); }
  };

  const handleTogglePreview = async (id) => {
    try {
      const res = await api.patch(`/api/materials/${id}/preview`);
      setMaterials(prev => prev.map(m => m.material_id === id ? { ...m, is_preview: res.data.is_preview } : m));
    } catch { message.error('Failed to update preview'); }
  };

  const handleSaveGenerated = async (blob, displayName, subjectId, aiType, mimeType, s3FileName) => {
    try {
      const uploadKey = s3FileName || displayName;
      const urlRes = await api.get('/api/materials/upload-url', {
        params: { fileName: uploadKey, fileType: mimeType },
      });
      await fetch(urlRes.data.uploadURL, {
        method: 'PUT', body: blob, headers: { 'Content-Type': mimeType },
      });
      const s3Key = urlRes.data.uploadURL.split('?')[0];
      await api.post('/api/materials', {
        user_id: user.userId,
        subject_id: subjectId || null,
        material_link: s3Key,
        file_name: displayName,    // display name (renameable) — not the S3 key
        ai_type: aiType || null,
      });
      loadAll();
    } catch { message.error('Failed to save'); }
  };

  // Subjects available for the selected tutor (or all tutors if none selected)
  const visibleSubjects = (() => {
    const source = selTutor ? materials.filter(m => m.tutor_name === selTutor) : materials;
    const seen = new Set();
    return source.reduce((acc, m) => {
      if (m.subject_name && !seen.has(m.subject_name)) {
        seen.add(m.subject_name);
        acc.push(m.subject_name);
      }
      return acc;
    }, []);
  })();

  const filtered = materials.filter(m => {
    if (selAiType) {
      if (selAiType === 'manual' && m.ai_type) return false;
      if (selAiType !== 'manual' && m.ai_type !== selAiType) return false;
    }
    if (user.role === 'student') {
      if (selTutor && m.tutor_name !== selTutor) return false;
      if (selSubject && m.subject_name !== selSubject) return false;
      if (search && !(m.file_name || '').toLowerCase().includes(search.toLowerCase())) return false;
    } else {
      if (selSubject && m.subject_id !== selSubject) return false;
      if (search && !(m.file_name || '').toLowerCase().includes(search.toLowerCase())) return false;
    }
    return true;
  });
  const selectedSubjectName = subjects.find(s => s.subject_id === selSubject)?.subject_name;

  return (
    <AppLayout>
      <div className="page-header">
        <h1>Materials</h1>
        <p>Upload and manage study notes, past papers, and resources.</p>
      </div>

      {user.role !== 'student' && (
        <>
          <div style={{ background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#0958d9' }}>
            💡 Click the <EyeOutlined /> icon on any material to make it visible to students as a preview before they subscribe.
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <Upload beforeUpload={handleUpload} showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
              <Button icon={<UploadOutlined />} type="primary" style={{ background: '#0a0a0a', border: 'none' }}>
                Upload file
              </Button>
            </Upload>
            <Button icon={<ThunderboltOutlined />} onClick={() => setAiOpen(true)}
              style={{ borderColor: '#fa8c16', color: '#fa8c16' }}>
              Generate with AI
            </Button>
            {subjects.length === 0 && (
              <span style={{ fontSize: 12, color: '#aaa' }}>Add subjects on your profile first</span>
            )}
          </div>
        </>
      )}

      <Modal
        title="Upload file"
        open={uploadModal}
        maskClosable={false}
        onCancel={() => { setUploadModal(false); setPendingFile(null); setUploadName(''); setSelSubject(null); setSelUploadType(null); }}
        onOk={confirmUpload}
        okText="Upload"
        confirmLoading={uploading}
        okButtonProps={{ style: { background: '#0a0a0a', border: 'none' } }}
      >
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>File name</div>
            <Input
              value={uploadName}
              onChange={e => setUploadName(e.target.value)}
              placeholder="Enter a name for this file"
              autoFocus
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Subject (optional)</div>
            <Select
              style={{ width: '100%' }} placeholder="Select subject"
              value={selSubject || undefined} onChange={setSelSubject} allowClear
              options={subjects.map(s => ({ value: s.subject_id, label: `${s.subject_name} (${s.level})` }))}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Type (optional)</div>
            <Select
              style={{ width: '100%' }} placeholder="Select type"
              value={selUploadType || undefined} onChange={setSelUploadType} allowClear
              options={TYPE_OPTIONS}
            />
          </div>
        </div>
      </Modal>

      {/* ── Tutor search + filter bar ── */}
      {user.role !== 'student' && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#ccc' }} />}
            placeholder="Search materials…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width: 220 }}
          />
          {subjects.length > 0 && (
            <Select
              style={{ width: 200 }} placeholder="All subjects" allowClear
              value={selSubject || undefined}
              onChange={v => setSelSubject(v || null)}
              options={subjects.map(s => ({ value: s.subject_id, label: `${s.subject_name} (${s.level})` }))}
            />
          )}
          <Select
            style={{ width: 160 }} placeholder="All types" allowClear
            value={selAiType || undefined}
            onChange={v => setSelAiType(v || null)}
            options={TYPE_OPTIONS}
          />
          {(search || selSubject || selAiType) && (
            <Button size="small" onClick={() => { setSearch(''); setSelSubject(null); setSelAiType(null); }}>
              Clear filters
            </Button>
          )}
        </div>
      )}

      {/* ── Student search + filter UI ── */}
      {user.role === 'student' && (
        <>
          {/* Search + dropdowns */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
            <Input
              prefix={<SearchOutlined style={{ color: '#ccc' }} />}
              placeholder="Search materials…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
              style={{ width: 240 }}
            />
            {tutors.length > 1 && (
              <Select
                style={{ width: 180 }} placeholder="All tutors" allowClear
                value={selTutor || undefined}
                onChange={v => { setSelTutor(v || null); setSelSubject(null); }}
                options={tutors.map(t => ({ value: t.tutor_name, label: t.tutor_name }))}
              />
            )}
            {visibleSubjects.length > 0 && (
              <Select
                style={{ width: 200 }} placeholder="All subjects" allowClear
                value={selSubject || undefined}
                onChange={v => setSelSubject(v || null)}
                options={visibleSubjects.map(name => ({ value: name, label: name }))}
              />
            )}
            <Select
              style={{ width: 160 }} placeholder="All types" allowClear
              value={selAiType || undefined}
              onChange={v => setSelAiType(v || null)}
              options={TYPE_OPTIONS}
            />
            {(search || selTutor || selSubject || selAiType) && (
              <Button size="small" onClick={() => { setSearch(''); setSelTutor(null); setSelSubject(null); setSelAiType(null); }}>
                Clear filters
              </Button>
            )}
          </div>

          {/* Results count */}
          {!loading && (
            <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
              {filtered.length} file{filtered.length !== 1 ? 's' : ''}
              {search ? ` matching "${search}"` : ''}
            </p>
          )}

          {/* Card grid */}
          {loading
            ? <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
            : filtered.length === 0
              ? <Empty description="No materials found." style={{ padding: 60 }} />
              : (
                <Row gutter={[16, 16]}>
                  {filtered.map(item => {
                    const fname = (item.file_name || '').toLowerCase();
                    const isPdf = fname.endsWith('.pdf');
                    const isMd  = fname.endsWith('.md');
                    const isDoc = fname.endsWith('.doc') || fname.endsWith('.docx');
                    const FileIcon = isPdf ? FilePdfOutlined : isDoc ? FileWordOutlined : isMd ? FileTextOutlined : FilePdfOutlined;
                    const iconColor = isPdf ? '#ff4d4f' : isDoc ? '#2b579a' : isMd ? '#1677ff' : '#888';
                    return (
                      <Col xs={24} sm={12} md={8} lg={6} key={item.material_id}>
                        <a href={item.material_link} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                          <Card
                            variant="outlined"
                            hoverable
                            style={{ borderColor: '#e8e8e8', height: '100%' }}
                            bodyStyle={{ padding: '16px' }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
                              <FileIcon style={{ fontSize: 32, color: iconColor }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#0a0a0a', lineHeight: 1.4,
                                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                  {item.file_name || 'Unnamed file'}
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                                {item.tutor_name && <Tag style={{ fontSize: 10, margin: 0 }}>{item.tutor_name}</Tag>}
                                {item.subject_name && (
                                  <Tag style={{ fontSize: 10, margin: 0 }}>
                                    {item.subject_name}{item.level ? ` ${item.level}` : ''}
                                  </Tag>
                                )}
                                {item.ai_type && (
                                  <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                                    {item.ai_type.replace(/_/g, ' ')}
                                  </Tag>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: '#aaa' }}>
                                {dayjs(item.uploaded_at).format('D MMM YYYY')}
                              </div>
                            </div>
                          </Card>
                        </a>
                      </Col>
                    );
                  })}
                </Row>
              )
          }
        </>
      )}

      {/* ── Tutor/admin card grid ── */}
      {user.role !== 'student' && (
        <>
          {!loading && (
            <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
              {filtered.length} file{filtered.length !== 1 ? 's' : ''}
              {search ? ` matching "${search}"` : ''}
            </p>
          )}
          {loading
            ? <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
            : filtered.length === 0
              ? <Empty description="No materials yet." style={{ padding: 60 }} />
              : (
                <Row gutter={[16, 16]}>
                  {filtered.map(item => {
                    const fname = (item.file_name || '').toLowerCase();
                    const isPdf = fname.endsWith('.pdf');
                    const isMd  = fname.endsWith('.md');
                    const isDoc = fname.endsWith('.doc') || fname.endsWith('.docx');
                    const FileIcon = isPdf ? FilePdfOutlined : isDoc ? FileWordOutlined : isMd ? FileTextOutlined : FilePdfOutlined;
                    const iconColor = isPdf ? '#ff4d4f' : isDoc ? '#2b579a' : isMd ? '#1677ff' : '#888';
                    return (
                      <Col xs={24} sm={12} md={8} lg={6} key={item.material_id}>
                        <Card
                          variant="outlined"
                          style={{ borderColor: item.is_preview ? '#91caff' : '#e8e8e8', height: '100%' }}
                          bodyStyle={{ padding: '14px' }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
                            {/* Top: icon + actions */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <FileIcon style={{ fontSize: 28, color: iconColor }} />
                              <div style={{ display: 'flex', gap: 2 }}>
                                <Button size="small" type="text" icon={<EyeOutlined />}
                                  onClick={() => handleTogglePreview(item.material_id)}
                                  style={{ color: item.is_preview ? '#1677ff' : '#ccc' }}
                                  title={item.is_preview ? 'In preview — click to hide' : 'Not in preview — click to show'}
                                />
                                {editingId === item.material_id ? (
                                  <>
                                    <Button size="small" type="text" icon={<CheckOutlined />}
                                      style={{ color: '#52c41a' }} onClick={() => handleRename(item.material_id)} />
                                    <Button size="small" type="text" icon={<CloseOutlined />}
                                      onClick={() => setEditingId(null)} />
                                  </>
                                ) : (
                                  <Button size="small" type="text" icon={<EditOutlined />}
                                    onClick={() => { setEditingId(item.material_id); setEditingName(item.file_name || ''); }} />
                                )}
                                <Popconfirm title="Delete this material?" onConfirm={() => handleDelete(item.material_id)}>
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </div>
                            </div>

                            {/* File name (editable or link) */}
                            <div style={{ flex: 1 }}>
                              {editingId === item.material_id ? (
                                <Input value={editingName} onChange={e => setEditingName(e.target.value)}
                                  onPressEnter={() => handleRename(item.material_id)}
                                  onBlur={() => handleRename(item.material_id)}
                                  autoFocus size="small"
                                  style={{ fontSize: 13, fontWeight: 500 }} />
                              ) : (
                                <a href={item.material_link} target="_blank" rel="noreferrer"
                                  style={{ fontWeight: 600, fontSize: 13, color: '#0a0a0a', lineHeight: 1.4,
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden', textDecoration: 'none', cursor: 'pointer' }}>
                                  {item.file_name || 'Unnamed file'}
                                </a>
                              )}
                            </div>

                            {/* Tags */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {item.subject_name && (
                                <Tag style={{ fontSize: 10, margin: 0 }}>
                                  {item.subject_name}{item.level ? ` ${item.level}` : ''}
                                </Tag>
                              )}
                              {item.ai_type && (
                                <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                                  {item.ai_type.replace(/_/g, ' ')}
                                </Tag>
                              )}
                              {item.is_preview && (
                                <Tag color="cyan" style={{ fontSize: 10, margin: 0 }}>Preview</Tag>
                              )}
                            </div>

                            <div style={{ fontSize: 11, color: '#aaa' }}>
                              {dayjs(item.uploaded_at).format('D MMM YYYY')}
                            </div>
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )
          }
        </>
      )}

      <AIGeneratorPanel
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        subject={selSubject}
        subjects={subjects}
        onSave={handleSaveGenerated}
      />
    </AppLayout>
  );
}
